import React, { useRef, useState } from 'react';
import { FiUploadCloud } from 'react-icons/fi';
import '../../styles/office-tools.css';

function matchesAccept(file, accept) {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return accept.split(',').map((s) => s.trim().toLowerCase()).some((rule) => {
    if (!rule) return false;
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/**
 * Click-or-drop file picker used by the office tools. Wraps a hidden <input type="file">
 * (so the native picker still works) and highlights while a file is dragged over it.
 * `onFiles` receives an array of File objects, or `onInputChange` the raw input event.
 */
export default function FileDrop({
  accept,
  multiple = false,
  disabled = false,
  title,
  hint,
  className = '',
  onFiles,
  onInputChange,
  ...rest
}) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const deliver = (fileList, event) => {
    if (onInputChange && event) {
      onInputChange(event);
      return;
    }
    onFiles?.(Array.from(fileList || []));
  };

  const onDrop = (event) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (disabled) return;
    // Ignore dropped files the picker wouldn't accept, so they never reach the server.
    let files = Array.from(event.dataTransfer?.files || []).filter((f) => matchesAccept(f, accept));
    if (!multiple) files = files.slice(0, 1);
    if (!files.length) return;
    if (onInputChange) {
      onInputChange({ target: { files, value: '' } });
    } else {
      onFiles?.(files);
    }
  };

  return (
    <label
      className={`nb-drop ${dragging ? 'is-dragging' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}
      onDragEnter={(e) => { e.preventDefault(); depth.current += 1; setDragging(true); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setDragging(false); }}
      onDrop={onDrop}
      {...rest}
    >
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => deliver(e.target.files, e)}
      />
      <span className="nb-drop-icon" aria-hidden="true"><FiUploadCloud /></span>
      <span className="nb-drop-title">{dragging ? 'Drop to add' : title}</span>
      {hint && <span className="nb-drop-hint">{hint}</span>}
    </label>
  );
}
